import { computed, type Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  type WorkspaceIdentity,
  type WorkspaceIdentityService,
} from '@modules/common/services/workspace-identity.service';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  type Observable,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';

/** Panel labels may resolve the saved owner; creation and request identities stay ambient. */
export function itemDisplayIdentity(
  current: Signal<WorkspaceIdentity | undefined>,
  service: WorkspaceIdentityService | null,
  changed: Observable<void>,
  item: () => { id?: string; accountId?: string } | undefined,
  panel: () => boolean,
): Signal<WorkspaceIdentity | undefined> {
  const owner = changed.pipe(
    map(() => {
      const saved = item();
      return saved?.id && panel()
        ? { itemId: saved.id, owner: saved.accountId }
        : { itemId: undefined, owner: undefined };
    }),
    distinctUntilChanged((previous, next) => previous.itemId === next.itemId && previous.owner === next.owner),
  );
  const fallback = toSignal(
    combineLatest([toObservable(current), owner]).pipe(
      switchMap(([identity, target]) =>
        !identity && target.owner && service
          ? service.forItemOwner(target.owner).pipe(
              take(1),
              catchError(() => of(undefined)),
              startWith(undefined),
            )
          : of(undefined),
      ),
    ),
    { initialValue: undefined },
  );
  return computed(() => current() ?? fallback());
}
